import {
  getBasketballHandicapBet,
  getBasketballOverUnderBet,
  getGoalsOverUnderBet,
  getDoubleChanceBet,
  getRestOfMatchBet,
  getNextGoalBet,
  getSetWinnerBet,
  getTennisOverUnderBet,
} from 'helpers/matchHelper';
import { SportIDs } from 'types/enums';
import { translateOddText } from 'helpers/betI18n';
import type { TFunction } from 'i18next';
import {
  BasketballLiveEvent,
  LiveEvent,
  LiveEventBet,
  LiveEventOdd,
  TennisLiveEvent,
} from 'types/LiveEvent';

/**
 * Modern live list: the kinds of odds a row can show, picked by chips over
 * the list - the same markets the desktop live table has as columns.
 * `full` is the row's own 1 X 2.
 */
export type LiveMarketKey =
  | 'full'
  | 'remaining'
  | 'doubleChance'
  | 'nextGoal'
  | 'setWinner'
  | 'handicap'
  | 'overUnder'
  // Any other market of the feed, by its name.
  | `bet:${string}`;

export type LiveMarket = {
  key: LiveMarketKey;
  /** Translation key of the chip. */
  label: string;
  /** The market in an event, null when it has none just now. */
  pick?: (event: LiveEvent) => LiveEventBet | null | undefined;
  /** A totals market: its line goes next to the name, picks read + / -. */
  totals?: boolean;
  /** Any other market: the feed's name (the tab reads it translated), and
   *  the row shows the whole market as in the match's details. */
  feedName?: string;
  /** The tab's own name (a line of a market: "Više/manje 2.5"). */
  title?: (t: TFunction) => string;
};

const FULL: LiveMarket = { key: 'full', label: 'market_group_main' };

export const liveMarketsFor = (sportId: number): LiveMarket[] => {
  switch (sportId) {
    case SportIDs.SOCCER:
      // Next goal right after 1X2 (owner, 2026-10-30).
      return [
        FULL,
        { key: 'nextGoal', label: 'next_goal_label', pick: getNextGoalBet },
        {
          key: 'remaining',
          label: 'match_remaining_label',
          pick: getRestOfMatchBet,
        },
        {
          key: 'doubleChance',
          label: 'double_chance_label',
          pick: getDoubleChanceBet,
        },
        {
          key: 'overUnder',
          label: 'over_under_label',
          pick: getGoalsOverUnderBet,
          totals: true,
        },
      ];
    case SportIDs.TENNIS:
      return [
        FULL,
        {
          key: 'setWinner',
          label: 'set_winner_label',
          pick: (e) => getSetWinnerBet(e as TennisLiveEvent),
        },
        {
          key: 'overUnder',
          label: 'over_under_label',
          pick: (e) => getTennisOverUnderBet(e as TennisLiveEvent),
          totals: true,
        },
      ];
    case SportIDs.BASKETBALL:
      return [
        FULL,
        {
          key: 'handicap',
          label: 'handicap_label',
          pick: (e) => getBasketballHandicapBet(e as BasketballLiveEvent),
        },
        {
          key: 'overUnder',
          label: 'over_under_label',
          pick: (e) => getBasketballOverUnderBet(e as BasketballLiveEvent),
          totals: true,
        },
      ];
    default:
      return [FULL];
  }
};

/**
 * The tabs of a live list: the fixed ones the matches have just now, then
 * every other market some match offers (the "+25" of a row), the most
 * offered first, so all of them can be shown on the list.
 */
export const liveMarketsOn = (
  sportId: number,
  events: LiveEvent[],
  fullTimeName?: string
): LiveMarket[] => {
  const fixed = liveMarketsFor(sportId).filter(
    (m) =>
      // Football lists the goal lines 1.5 / 2.5 / 3.5 below; the main line
      // was the 2.5 again (owner, 2026-10-30: "ponavlja se dva puta").
      !(sportId === SportIDs.SOCCER && m.key === 'overUnder') &&
      (!m.pick || events.some((ev) => !!m.pick!(ev)?.odds?.length))
  );
  const covered = new Set<string>(fullTimeName ? [fullTimeName] : []);
  for (const m of fixed) {
    if (!m.pick) continue;
    for (const ev of events) {
      const name = m.pick(ev)?.name;
      if (name) covered.add(name);
    }
  }
  // The sports the owner listed the important markets of (2026-10-29,
  // after swisslos.ch): only those, in that order. Others: every market.
  const curated = CURATED[sportId];
  if (curated) {
    const offered = (list: Curated[]) =>
      list
        .map((c) => curatedMarket(c))
        .filter((m) => events.some((ev) => !!m.pick!(ev)?.odds?.length));
    // The ones the owner wants on top go right after the 1 X 2.
    const lead = offered(curated.filter((c) => c.lead));
    const others = offered(curated.filter((c) => !c.lead));
    return [...fixed.slice(0, 1), ...lead, ...fixed.slice(1), ...others];
  }
  const count = new Map<string, number>();
  for (const ev of events) {
    const seen = new Set<string>();
    for (const b of ev.bets ?? []) {
      if (!b?.name || covered.has(b.name) || seen.has(b.name)) continue;
      if (!b.odds?.length) continue;
      seen.add(b.name);
      count.set(b.name, (count.get(b.name) ?? 0) + 1);
    }
  }
  const others = Array.from(count.entries())
    .sort((a, b) => b[1] - a[1])
    .map(
      ([name]): LiveMarket => ({
        key: `bet:${name}`,
        label: name,
        feedName: name,
        pick: (ev) =>
          ev.bets?.find((b) => b.name === name && b.odds?.length > 0),
      })
    );
  return [...fixed, ...others];
};

/** One important market: the feed's markets it is read from (the first
 *  one the match has), and for one line of them which picks belong. */
type Curated = {
  key: string;
  names: RegExp;
  /** The feed's name the tab reads, translated (when no title). */
  label?: string;
  title?: (t: TFunction) => string;
  picks?: (odd: LiveEventOdd, event: LiveEvent) => boolean;
  /** Read the picks from every matching market (a line can be in the main
   *  market or in the alternatives). */
  merge?: boolean;
  /** Of several matching markets the one to show (the next game: the one
   *  with the lowest number); otherwise the first. */
  choose?: (names: string[]) => string;
  /** Listed right after the 1 X 2. */
  lead?: boolean;
  /** Over / under: the picks read + / −. */
  totals?: boolean;
};

/** The market whose set and game numbers come first ("Set 1 - Game 7 Winner"
 *  before "Set 1 - Game 8 Winner"): the game being played or the next one. */
const firstGame = (names: string[]) => {
  const order = (n: string) =>
    (n.match(/\d+/g) ?? []).map(Number).reduce((a, x) => a * 100 + x, 0);
  return names.reduce((a, b) => (order(b) < order(a) ? b : a));
};

/**
 * One more goal from the score now (owner, 2026-10-30, as betting shops
 * offer it): at 0:0 the 0.5 line, at 1:1 the 2.5 line - over is "another
 * goal", under "no more goals".
 */
const oneMoreGoal: Curated = {
  key: 'oneMore',
  names: /^(Match Goals|Alternative Match Goals)$/,
  merge: true,
  totals: true,
  lead: true,
  title: (t) => t('live_one_more_goal'),
  picks: (o, ev) => {
    const goals = (Number(ev.homeGoals) || 0) + (Number(ev.awayGoals) || 0);
    return Number(String(o.handicap ?? '').trim()) === goals + 0.5;
  },
};

const goalLine = (line: string): Curated => ({
  key: `goals${line}`,
  names: /^(Match Goals|Alternative Match Goals)$/,
  merge: true,
  title: (t) => `${t('over_under_label')} ${line}`,
  picks: (o) => String(o.handicap ?? '').trim() === line,
});

/** 3-way handicap line by the home side's handicap: -1 is "0:1". */
const handicapLine = (home: number): Curated => ({
  key: `hcp${home}`,
  names: /^3-Way Handicap$/,
  title: (t) =>
    `${t('handicap_label')} ${home < 0 ? `0:${-home}` : `${home}:0`}`,
  picks: (o, ev) => {
    const h = Number(String(o.handicap ?? '').trim());
    return (o.name === ev.home.name ? h : -h) === home;
  },
});

const CURATED: Record<number, Curated[]> = {
  [SportIDs.SOCCER]: [
    // From bwin (tottalo_api internal/bwin), while the first half is on:
    // who scores its next goal, or none.
    {
      key: 'nextGoalHalf',
      names: /^1st Half - Next Team to Score$/,
      title: (t) => t('live_next_goal_first_half'),
      lead: true,
    },
    {
      key: 'nextGoalHalf2',
      names: /^2nd Half - Next Team to Score$/,
      title: (t) => t('live_next_goal_second_half'),
      lead: true,
    },
    oneMoreGoal,
    {
      key: 'btts',
      names: /^Both Teams to Score$/,
      label: 'Both Teams to Score',
    },
    goalLine('1.5'),
    goalLine('2.5'),
    goalLine('3.5'),
    handicapLine(-1),
    handicapLine(1),
    { key: 'dnb', names: /^Draw No Bet$/, label: 'Draw No Bet' },
    { key: 'ht', names: /^Half Time Result$/, label: 'Half Time Result' },
    {
      key: 'htGoals',
      names: /^(1st Half Goals|1st Half Goal Line.*)$/,
      label: '1st Half Goals',
    },
    {
      key: 'htBtts',
      names: /^Both Teams to Score in 1st Half$/,
      label: 'Both Teams to Score in 1st Half',
    },
    {
      key: 'sh2Btts',
      names: /^Both Teams to Score in 2nd Half$/,
      label: 'Both Teams to Score in 2nd Half',
    },
    // From bwin (tottalo_api internal/bwin), while the half is on; both
    // teams to score above too where bet365 does not offer it.
    {
      key: 'htDc',
      names: /^1st Half - Double Chance$/,
      label: '1st Half - Double Chance',
    },
    {
      key: 'htResultGoals',
      names: /^1st Half - Result & Total Goals$/,
      label: '1st Half - Result & Total Goals',
    },
    {
      key: 'htExact',
      names: /^1st Half - Exact Goals$/,
      label: '1st Half - Exact Goals',
    },
    {
      key: 'sh2Dc',
      names: /^2nd Half - Double Chance$/,
      label: '2nd Half - Double Chance',
    },
    {
      key: 'sh2Goals',
      names: /^2nd Half - Total Goals$/,
      label: '2nd Half - Total Goals',
      // One line on the list (the first bwin gives); all are in the match.
      totals: true,
    },
    {
      key: 'htOddEven',
      names: /^1st Half - Goals Odd\/Even$/,
      label: '1st Half - Goals Odd/Even',
    },
  ],
  [SportIDs.ICE_HOCKEY]: [
    { key: 'goals', names: /^Alternative Game Totals$/, label: 'Game Totals' },
    { key: 'hcp', names: /^Alternative Puck Lines$/, label: 'Puck Line' },
    { key: 'dnb', names: /^Draw No Bet$/, label: 'Draw No Bet' },
    { key: 'next', names: /^\d+(st|nd|rd|th) Goal$/, label: 'Next Goal' },
    {
      key: 'p1',
      names: /^Period 1 Winner 3-Way$/,
      label: 'Period 1 Winner 3-Way',
    },
    {
      key: 'p2',
      names: /^Period 2 Winner 3-Way$/,
      label: 'Period 2 Winner 3-Way',
    },
    {
      key: 'p3',
      names: /^Period 3 Winner 3-Way$/,
      label: 'Period 3 Winner 3-Way',
    },
    {
      key: 'pBtts',
      names: /^Period \d Both Teams to Score$/,
      label: 'Period 1 Both Teams to Score',
    },
  ],
  [SportIDs.TENNIS]: [
    // Owner, 2026-10-30: players ask most for who wins the next game and
    // the next two games, so they come first.
    {
      key: 'nextGame',
      names: /^Set \d+\s*-?\s*Game \d+ Winner$/,
      title: (t) => t('live_next_game'),
      choose: firstGame,
      lead: true,
    },
    {
      key: 'nextTwoGames',
      names: /^Set \d+ - Games \d+ & \d+ - Winner$/,
      title: (t) => t('live_next_two_games'),
      choose: firstGame,
      lead: true,
    },
    {
      key: 'setHcp',
      label: 'Set Handicap',
      names: /^(Set Handicap|Match Handicap \(Sets\))$/,
    },
    {
      key: 'gamesHcp',
      label: 'Games Handicap',
      names: /^(Match Handicap|Match Handicap \(Games\)|Games Handicap)$/,
    },
  ],
  [SportIDs.HANDBALL]: [
    {
      key: 'goals',
      label: 'Match Goals',
      names: /^(Alternative )?(Match Goals|Game Totals|Total Goals)$/,
    },
    {
      key: 'hcp',
      label: 'Handicap',
      names: /^(Alternative )?(Handicap|Match Handicap|Game Handicap)$/,
    },
    { key: 'dnb', names: /^Draw No Bet$/, label: 'Draw No Bet' },
    {
      key: 'ht',
      label: 'Half Time Result',
      names: /^(Half Time Result|1st Half Result|1st Half Winner.*)$/,
    },
    {
      key: 'htGoals',
      label: '1st Half Goals',
      names: /^1st Half (Goals|Totals?)$/,
    },
    { key: 'htHcp', label: '1st Half Handicap', names: /^1st Half Handicap$/ },
  ],
  [SportIDs.VOLLEYBALL]: [
    { key: 'set', label: 'Set Winner', names: /^Set \d+ Winner$/ },
    {
      key: 'points',
      label: 'Total Points',
      names: /^(Total Points|Match Total Points|Game Totals)$/,
    },
    {
      key: 'hcp',
      label: 'Handicap',
      names: /^(Set Handicap|Points Handicap|Match Handicap)$/,
    },
  ],
};

const curatedMarket = (c: Curated): LiveMarket => ({
  key: `bet:${c.key}`,
  label: c.label ?? c.key,
  title: c.title,
  totals: c.totals,
  feedName: 'curated',
  pick: (ev) => {
    const found = (ev.bets ?? []).filter(
      (b) => b?.name && c.names.test(b.name) && b.odds?.length > 0
    );
    if (!found.length) return null;
    const chosen = c.choose
      ? found.find((b) => b.name === c.choose!(found.map((x) => x.name)))!
      : found[0];
    const from = c.merge ? found : [chosen];
    const odds = from
      .flatMap((b) => b.odds)
      .filter((o) => !c.picks || c.picks(o, ev));
    if (!odds.length) return null;
    return { ...from[0], odds };
  },
});

const DRAW_NAMES = /^(x|draw|tie|no \d+\w* goal|no goal)$/i;

/** A pick's short label: 1 / X / 2 for the teams and the draw (also "1X"
 *  for "Home or Draw"), + / - for totals, otherwise the feed's own text. */
export const livePickLabel = (
  event: LiveEvent,
  odd: LiveEventOdd,
  totals: boolean,
  t: TFunction
) => {
  const name = (odd.name ?? '').trim();
  if (totals)
    return /^u/i.test(name) || /^u/i.test(odd.header ?? '') ? '−' : '+';
  const side = (x: string) => {
    // Game markets mark the server: "Marie Vogt (Svr)".
    const v = x.replace(/\s*\(Svr\)\s*$/i, '').trim();
    if (v === '1' || v === event.home.name) return '1';
    if (v === '2' || v === event.away.name) return '2';
    if (DRAW_NAMES.test(v)) return 'X';
    return null;
  };
  const parts = name.split(/\s+or\s+/i).map(side);
  const short = parts.every(Boolean) ? parts.join('') : null;
  const label = short ?? translateOddText(name, t);
  return odd.handicap ? `${label} (${odd.handicap})` : label;
};
